import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Pencil, Search, Trash2 } from "lucide-react";

import { PageHeader } from "../../../components/admin/PageHeader";
import { Pagination, useSessionPageSize } from "../../../components/ui/Pagination";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { EmptyState } from "../../../components/feedback/EmptyState";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { adminUsersService } from "../../../services/adminUsers";
import { formatApiError } from "../../../services/api";
import { useAuth } from "../../../app/providers/AuthProvider";
import { UserFormModal } from "../components/UserFormModal";
import { ResetPasswordModal } from "../components/ResetPasswordModal";

const ROLE_BADGE = {
  standard: "bg-offwhite text-text-secondary border border-border-subtle",
  warehouse: "bg-status-progress/15 text-status-progress border border-status-progress/30",
  admin: "bg-champagne/25 text-charcoal border border-champagne/50",
};

function RoleBadge({ role, t }) {
  const label = t(`admin.users.role_${role}`);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${ROLE_BADGE[role] || ROLE_BADGE.standard}`}
      data-testid={`user-role-${role}`}
    >
      {label}
    </span>
  );
}

function IconAction({ icon: Icon, label, onClick, tone, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        // Row-level onClick navigates to /users/:id — stop propagation so the
        // action icons don't accidentally trigger a route change.
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      aria-label={label}
      title={label}
      data-testid={testId}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne ${
        tone === "danger"
          ? "text-status-unavailable hover:bg-status-unavailable/10"
          : tone === "warning"
          ? "text-taupe hover:bg-offwhite"
          : "text-text-primary hover:bg-offwhite"
      }`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

export default function UsersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useSessionPageSize("users.pageSize", 10);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState("email:asc");

  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Debounce search 300ms
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminUsersService.list({
        search: debouncedSearch || undefined,
        sort,
        page,
        page_size: pageSize,
      });
      setItems(d.items || []);
      setTotal(d.total || 0);
    } catch (err) {
      toast.error(formatApiError(err, t("common.error_generic")));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, sort, page, pageSize, t]);

  useEffect(() => { load(); }, [load]);

  // Quick action tile support (?new=1 → open create modal).
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setEditingUser(null);
      setFormOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openCreate = () => { setEditingUser(null); setFormOpen(true); };
  const openEdit = (u) => { setEditingUser(u); setFormOpen(true); };
  const openReset = (u) => { setResetTarget(u); setResetOpen(true); };
  const askDelete = (u) => { setDeleteError(null); setDeleteTarget(u); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await adminUsersService.remove(deleteTarget.id);
      toast.success(t("admin.users.deleted"));
      setDeleteTarget(null);
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "HAS_ACTIVE_ORDERS") {
        setDeleteError({
          code: "HAS_ACTIVE_ORDERS",
          count: d.count,
          orders: d.orders || [],
        });
      } else if (d && typeof d === "object" && d.code === "LAST_ADMIN") {
        setDeleteError({ code: "LAST_ADMIN" });
      } else if (d && typeof d === "object" && d.code === "SELF_DELETE") {
        setDeleteError({ code: "SELF_DELETE" });
      } else {
        toast.error(formatApiError(err, t("common.error_generic")));
      }
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(total, page * pageSize);
  const changeSort = (key) => {
    const [k, d] = sort.split(":");
    const nextDir = k === key && d === "asc" ? "desc" : "asc";
    setSort(`${key}:${nextDir}`);
    setPage(1);
  };
  const sortArrow = (key) => (sort.startsWith(`${key}:`) ? (sort.endsWith(":asc") ? "↑" : "↓") : "");

  return (
    <>
      <div className="space-y-6" data-testid="users-page">
        <PageHeader
          title={t("admin.users.title")}
          subtitle={t("admin.users.subtitle")}
          actionLabel={t("admin.users.add_new_cta")}
          onAction={openCreate}
          actionTestId="users-add-new"
        />

        <div className="rounded-lg border border-border-subtle bg-surface p-4 shadow-card">
          <div className="mb-4 flex items-center gap-2">
            <Search className="h-4 w-4 text-text-secondary" aria-hidden />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("admin.users.search_placeholder")}
              data-testid="users-search"
              className="h-9 max-w-md bg-surface border-border-subtle"
            />
          </div>

          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-11 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState title={t("admin.users.empty_title")} description={t("admin.users.empty_body")} />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="users-table">
                <thead className="bg-offwhite text-left text-[11px] uppercase tracking-wide text-text-secondary">
                  <tr>
                    {[
                      ["email", "admin.users.col_email"],
                      ["first_name", "admin.users.col_first_name"],
                      ["last_name", "admin.users.col_last_name"],
                    ].map(([k, l]) => (
                      <th key={k} className="px-4 py-3 font-semibold">
                        <button
                          type="button"
                          onClick={() => changeSort(k)}
                          data-testid={`users-sort-${k}`}
                          className="inline-flex items-center gap-1 hover:text-text-primary"
                        >
                          {t(l)} {sortArrow(k)}
                        </button>
                      </th>
                    ))}
                    <th className="px-4 py-3 font-semibold">{t("admin.users.col_phone")}</th>
                    <th className="px-4 py-3 font-semibold">
                      <button type="button" onClick={() => changeSort("role")} data-testid="users-sort-role"
                        className="inline-flex items-center gap-1 hover:text-text-primary">
                        {t("admin.users.col_role")} {sortArrow("role")}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-right font-semibold">{t("admin.users.col_actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {items.map((u) => {
                    const isSelf = u.id === currentUser?.id;
                    return (
                      <tr
                        key={u.id}
                        data-testid={`user-row-${u.id}`}
                        onClick={() => navigate(`/users/${u.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(`/users/${u.id}`);
                          }
                        }}
                        tabIndex={0}
                        role="link"
                        aria-label={`${u.first_name} ${u.last_name}`.trim()}
                        className="cursor-pointer transition-colors hover:bg-offwhite/40 focus-visible:bg-offwhite/60 focus-visible:outline-none"
                      >
                        <td className="px-4 py-3 font-mono text-xs text-text-primary" data-testid={`user-row-email-${u.id}`}>
                          {u.email}
                          {isSelf && (
                            <span className="ml-2 rounded bg-champagne/30 px-1.5 py-0.5 text-[10px] font-semibold text-charcoal">
                              {t("admin.users.you")}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-text-primary">{u.first_name}</td>
                        <td className="px-4 py-3 text-text-primary">{u.last_name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-text-secondary">{u.phone || "—"}</td>
                        <td className="px-4 py-3"><RoleBadge role={u.role} t={t} /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <IconAction
                              icon={Pencil}
                              label={t("common.edit")}
                              onClick={() => openEdit(u)}
                              tone="warning"
                              testId={`user-action-edit-${u.id}`}
                            />
                            <IconAction
                              icon={KeyRound}
                              label={t("admin.users.reset_password_tooltip")}
                              onClick={() => openReset(u)}
                              testId={`user-action-reset-${u.id}`}
                            />
                            {!isSelf && (
                              <IconAction
                                icon={Trash2}
                                label={t("common.delete")}
                                onClick={() => askDelete(u)}
                                tone="danger"
                                testId={`user-action-delete-${u.id}`}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 && (
            <div className="mt-4">
              <Pagination
                page={page}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                total={total}
                testId="users-pagination"
              />
            </div>
          )}
        </div>
      </div>

      <UserFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        editingUser={editingUser}
        onSaved={load}
      />

      <ResetPasswordModal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        user={resetTarget}
      />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="border-border-subtle bg-surface" data-testid="user-delete-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.users.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-text-secondary">
                <div>
                  {t("admin.users.delete_body", {
                    name: `${deleteTarget?.first_name || ""} ${deleteTarget?.last_name || ""}`.trim(),
                  })}
                </div>
                {deleteError?.code === "HAS_ACTIVE_ORDERS" && (
                  <div
                    className="rounded-md border border-status-unavailable/30 bg-status-unavailable/5 p-3 text-status-unavailable"
                    data-testid="user-delete-error-orders"
                  >
                    <div className="font-medium">
                      {t("admin.users.err_active_orders", { count: deleteError.count })}
                    </div>
                    <ul className="mt-2 space-y-1 text-xs">
                      {deleteError.orders.map((o) => (
                        <li key={o.id} className="font-mono">
                          #{o.order_number || o.id.slice(0, 8)} · {o.event_name} · {o.status}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {deleteError?.code === "LAST_ADMIN" && (
                  <div className="rounded-md border border-status-unavailable/30 bg-status-unavailable/5 p-3 text-status-unavailable">
                    {t("admin.users.err_last_admin")}
                  </div>
                )}
                {deleteError?.code === "SELF_DELETE" && (
                  <div className="rounded-md border border-status-unavailable/30 bg-status-unavailable/5 p-3 text-status-unavailable">
                    {t("admin.users.err_self_delete")}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Radix closes the dialog on click by default. Prevent that
                // so async 409 errors (HAS_ACTIVE_ORDERS / LAST_ADMIN /
                // SELF_DELETE) can render their inline blocks. We close
                // the dialog manually on success inside confirmDelete().
                e.preventDefault();
                confirmDelete();
              }}
              disabled={deleting || deleteError?.code === "HAS_ACTIVE_ORDERS" || deleteError?.code === "LAST_ADMIN"}
              data-testid="user-delete-confirm"
              className="bg-status-unavailable text-white hover:bg-status-unavailable/90"
            >
              {t("admin.users.delete_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
